import { cn } from "~/lib/utils";
import { Emdash } from "../Punctuation";
import { Tooltip } from "../Tooltip";
import { formatApy } from "~/utils/functions/formatApy";
import { GridPercentSign } from "./GridPercentSign";
import { DataGridPercentValue } from "./DataGridPercentValue";

// // // //

function isNumberInvalid(value: any) {
    return (
        value === undefined ||
        value === null ||
        typeof value !== "number" ||
        Number.isNaN(value)
    );
}

/**
 * DataGridPercentChangeArrow
 * Render a percent-change widget in DataGridCell
 */
function DataGridPercentChangeArrow(props: { value: number }) {
    const { value } = props;

    if (typeof value !== "number" || Number.isNaN(value)) {
        return <Emdash />;
    }

    if (value === 0) {
        return (
            <span className="text-secondary-foreground cursor-default invisible">
                {"△"}
            </span>
        );
    }

    const isPositive = value >= 0;
    const arrowIcon = isPositive ? "▲" : "▼";
    const colorClass = isPositive ? "text-change-up" : "text-change-down";

    return (
        <span className={cn(colorClass, "cursor-default")}>{arrowIcon}</span>
    );
}

function DataGridNumberValue(props: {
    value: number;
    significantDigits: number;
}) {
    const { value, significantDigits } = props;

    if (typeof value === "number") {
        return (
            <>
                {value.toLocaleString("en-US", {
                    maximumFractionDigits: significantDigits,
                    minimumFractionDigits: significantDigits,
                })}
            </>
        );
    }

    return <>{value}</>;
}

export function DataGridNumberPercentChangeComboCell(props: {
    value: null | number;
    pct_change_value: null | number;
    pct_change_interval?: "7d" | "30d";
    significantDigits?: number;
    type?: "number" | "percent";
}) {
    const {
        value,
        pct_change_interval = "7d",
        type = "number",
        significantDigits = 0,
    } = props;
    const pct_change_value = props.pct_change_value || 0;

    if (isNumberInvalid(value)) {
        return <Emdash />;
    }

    // Render content without tooltip when change is 0
    if (pct_change_value === 0) {
        return (
            <span className="flex items-center">
                {type === "number" && (
                    <DataGridNumberValue
                        value={value as number}
                        significantDigits={significantDigits}
                    />
                )}

                {type === "percent" && (
                    <DataGridPercentValue value={value as number} />
                )}
                <div className="ml-1.5">
                    <DataGridPercentChangeArrow
                        value={pct_change_value as number}
                    />
                </div>
            </span>
        );
    }

    return (
        <Tooltip
            placement="right"
            tooltipWrapperClassName={cn("text-white rounded-lg", {
                "bg-green-500 border-green-500": pct_change_value > 0,
                "bg-red-500 border-red-500": pct_change_value < 0,
                "bg-black border-black": pct_change_value === 0,
            })}
            tooltipContent={
                <div className="w-max px-2 py-1">
                    <p
                        className={cn(
                            "whitespace-nowrap text-xs tabular-nums text-center",
                            {
                                "text-foreground": pct_change_value === 0,
                            },
                            // value_7d_percent_change >= 0 ? "text-change-up" : "text-change-down"
                        )}
                    >
                        {pct_change_value === 0 && "No Change"}
                        {pct_change_value !== 0 && (
                            // <DataGridPercentChangeValue
                            //     value={value_7d_percent_change}
                            // />
                            <>
                                {pct_change_value >= 0 ? "+" : "-"}
                                &nbsp;
                                {parseFloat(
                                    Math.abs(
                                        formatApy(pct_change_value),
                                    ).toFixed(2),
                                ).toLocaleString()}
                                <GridPercentSign />
                                <span className="ml-0.5">
                                    {pct_change_interval === "7d"
                                        ? "from 7d ago"
                                        : "from 30d ago"}
                                </span>
                            </>
                        )}
                    </p>
                </div>
            }
        >
            {({ setTriggerRef }) => (
                <span className="flex items-center" ref={setTriggerRef}>
                    {type === "number" && (
                        <DataGridNumberValue
                            value={value as number}
                            significantDigits={significantDigits}
                        />
                    )}

                    {type === "percent" && (
                        <DataGridPercentValue value={value as number} />
                    )}
                    <div className="ml-1.5">
                        <DataGridPercentChangeArrow
                            value={pct_change_value as number}
                        />
                    </div>
                </span>
            )}
        </Tooltip>
    );
}
